"use client";
import { useAppSelector } from "../../../app/GlobalRedux/store";
import InfoSection from "@/components/InfoSection/InfoSection";
import LoadingState from "@/components/states/LoadingState";
import PaginationWrapper from "@/components/PaginationWrapper/PaginationWrapper";
import SearchPageLayout from "@/components/Layouts/SearchPageLayout/SearchPageLayout";
import Table from "@/components/Table/Table";
import TableControls from "@/components/Table/TableControls/TableControls";
import HibtService from "@/services/hibtService";
import { LiteratureSearchResult, SearchType } from "@/types";
import { itemsPerPageOptions } from "@/utils/consts";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState, useTransition } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-hook";
import EmptyState from "@/components/states/EmptyState/EmptyState";
import Logging from "@/utils/Logging";

const LiteratureSearchResultPage = () => {
  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    process.env.NEXT_PUBLIC_REACT_GACAPTV3_SITE_KEY || "",
    { hide: true }
  );

  const currentSearchValue = useAppSelector((state) => state.search.value);
  const searchParams = useSearchParams();
  const searchValue =
    searchParams.get("search_literature") || currentSearchValue;
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  // data state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dataTotal, setDataTotal] = useState(0);
  const [data, setData] = useState<LiteratureSearchResult[]>([]);
  const [dataToDisplay, setDataToDisplay] = useState<LiteratureSearchResult[]>(
    []
  );

  // pagination items
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageOptions[1].value
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    count: 0,
    from: 0,
    to: itemsPerPage,
  });

  const loadSearchResultsData = async () => {
    try {
      setIsLoading(true);

      const captchaKey = await executeGoogleReCaptcha("submit");

      const { records, total } = await HibtService.searchLiterature({
        text: searchValue.toString(),
        CaptchaResponse: captchaKey,
      });

      const transformedData = await records.map((searchResult) => ({
        ...searchResult,
        isChecked: false,
      }));

      setData(transformedData);
      setDataTotal(total);
      setPagination({ ...pagination, count: total });
      setIsLoading(false);

      startTransition(() => {
        router.refresh();
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      Logging.networkError("API Error - searchLiterature", {
        pageName: "LiteratureSearchResultPage",
        errorMessage: error.message,
        errorCode: error.code,
        statusCode: error.response?.status,
      });
    }
  };

  useEffect(() => {
    loadSearchResultsData();
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const paginatedData = data.slice(pagination.from, pagination.to);

    setDataToDisplay(paginatedData);
  }, [data, pagination.from, pagination.to]);

  useEffect(() => {
    // calculate the new page number based on the new items per page
    let newPageNumber = Math.ceil((pagination.from + 1) / itemsPerPage);
    if (newPageNumber <= 0) newPageNumber = 1;
    setCurrentPage(newPageNumber);

    // calculate new from and to
    const from = (newPageNumber - 1) * itemsPerPage;
    const to = itemsPerPage * newPageNumber;

    setPagination({ ...pagination, from, to });
  }, [itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    event.preventDefault();
    const from = (page - 1) * itemsPerPage;
    const to = itemsPerPage * page;

    setCurrentPage(page);
    setPagination({ ...pagination, from, to });
    if (window && typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const handleCheckBoxClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    url: string
  ) => {
    const { checked } = e.target;

    const newSelectionArray = dataToDisplay.map((searchResult) =>
      searchResult.url === url
        ? { ...searchResult, isChecked: checked }
        : searchResult
    );

    setDataToDisplay(newSelectionArray);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    url: string
  ) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).checked = true;

      const { checked } = e.target as HTMLInputElement;

      const newSelectionArray = dataToDisplay.map((searchResult) =>
        searchResult.url === url
          ? { ...searchResult, isChecked: checked }
          : searchResult
      );

      setDataToDisplay(newSelectionArray);
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      (e.target as HTMLInputElement).checked = false;

      const { checked } = e.target as HTMLInputElement;

      const newSelectionArray = dataToDisplay.map((searchResult) =>
        searchResult.url === url
          ? { ...searchResult, isChecked: checked }
          : searchResult
      );

      setDataToDisplay(newSelectionArray);
    }
  };

  const handleOnSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    const selectAllItems = dataToDisplay.map((searchResult) => ({
      ...searchResult,
      isChecked: checked,
    }));

    setDataToDisplay(selectAllItems);
  };

  const handleKeyDownSelectAll = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).checked = true;

      const { checked } = e.target as HTMLInputElement;

      const selectAllItems = dataToDisplay.map((searchresult) => ({
        ...searchresult,
        isChecked: checked,
      }));

      setDataToDisplay(selectAllItems);
    }
  };

  if (isLoading || isPending) {
    return <LoadingState />;
  }

  return (
    <SearchPageLayout>
      <InfoSection
        valueSearched={{
          value: searchValue || "",
          type: SearchType.LITERATURE,
        }}
        label="Text search"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        paginationCount={pagination.count}
        handlePageChange={handlePageChange}
      />

      <TableControls
        totalItems={dataTotal}
        pageNumber={currentPage}
        displayedItems={dataToDisplay.length}
        checkedItems={dataToDisplay.filter(({ isChecked }) => isChecked).length}
        itemsPerPage={itemsPerPage}
        itemsPerPageOptions={itemsPerPageOptions}
        setItemsPerPage={setItemsPerPage}
        handleOnSelectAllClick={handleOnSelectAllClick}
        handleKeyDownSelectAll={handleKeyDownSelectAll}
      />

      {dataToDisplay.length > 0 && !isLoading ? (
        <Table
          dataToDisplay={dataToDisplay}
          handleUrlCheckBoxClick={handleCheckBoxClick}
          handleUrlKeyDown={handleKeyDown}
        />
      ) : (
        <EmptyState>
          We were unable to find any results for your search. Please try again
        </EmptyState>
      )}

      <PaginationWrapper
        currentPage={currentPage}
        paginationCount={pagination.count}
        itemsPerPage={itemsPerPage}
        handlePageChange={handlePageChange}
      />
    </SearchPageLayout>
  );
};

export default LiteratureSearchResultPage;
